/**
 * The self-camera picture-in-picture geometry (the 1:1 call fix): while a call has exactly two
 * participants the own camera floats as a PiP tile over the dominant remote stream. This module
 * owns the pure math and the persistence half - the component (SelfPip) only translates pointer
 * and keyboard gestures into calls here.
 *
 * Sizing is by AREA, never by width alone: the default PiP covers ~20% of the main area, the
 * user can resize between ~12% (below that the tile is unusable) and a hard cap of 30% of the
 * main area. The 16:9 meeting aspect is kept, so one width derives from the area share:
 * w*h = w^2/ASPECT = share * W * H  =>  w = sqrt(share * W * H * ASPECT).
 *
 * Positioning is always CLAMPED fully inside the main area - the PiP never slides under the
 * control bar or off-screen, and a drag or a viewport change can push it at most back to an
 * edge. The persisted layout stores FRACTIONS of the main area, so a restore survives viewport
 * changes; the restore re-clamps into the current area.
 */

import type { KeyValueStorage } from './devicePrefs';
import { TILE_ASPECT } from './gridLayout';

/** The localStorage key holding the remembered PiP layout (deliberately outside devicePrefs). */
export const PIP_STORAGE_KEY = 'vc.pipLayout';

/** The area share the PiP opens with. */
export const PIP_DEFAULT_AREA_SHARE = 0.2;

/** The smallest usable area share; the resize (and the clamp) never shrink below it. */
export const PIP_MIN_AREA_SHARE = 0.12;

/** The hard area cap the user order asks for: at most 30% of the main area. */
export const PIP_MAX_AREA_SHARE = 0.3;

/** Inset from the main-area edges (the tile gutter token, same as the grid gap). */
export const PIP_MARGIN_PX = 12;

/**
 * Breathing gap kept between the PiP and the bottom inset band (the mobile control pill,
 * BR-DM-5): the clamp zone excludes the band plus this clearance.
 */
export const PIP_PILL_CLEARANCE_PX = 8;

/** One keyboard arrow press moves the PiP by this many pixels. */
export const PIP_KEYBOARD_MOVE_PX = 16;

/** One +/- keyboard press grows/shrinks the PiP width by this many pixels. */
export const PIP_KEYBOARD_RESIZE_PX = 48;

/**
 * The measured main area the PiP floats in (the meeting-layout section box).
 * `insetBottom` (BR-DM-5) is the bottom band kept clear of the clamp - on the mobile layout
 * the fixed control pill floats over the section, so the band (the pill's on-screen overlap
 * with the section plus a clearance) is excluded; absent/0 on the desktop layout.
 */
export interface PipArea {
  readonly width: number;
  readonly height: number;
  readonly insetBottom?: number;
}

/** The clampable height: the area height minus the excluded bottom band (never below 0). */
export function usableHeight(area: PipArea): number {
  return Math.max(0, area.height - (area.insetBottom ?? 0));
}

/** The PiP rectangle relative to the main area's top-left corner, in CSS pixels. */
export interface PipBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The 16:9 width that covers `share` of the area, bounded so the tile fits the usable area
 * (a very wide or very short area must not produce a tile wider/taller than the clampable
 * region - the bottom band excluded by `insetBottom` does not count, BR-DM-5).
 */
function widthForAreaShare(share: number, area: PipArea): number {
  const byArea = Math.sqrt(share * area.width * area.height * TILE_ASPECT);
  return Math.min(byArea, area.width, usableHeight(area) * TILE_ASPECT);
}

/** The largest width the 30% area cap (and the area bounds) allow. */
export function maxPipWidth(area: PipArea): number {
  return Math.max(1, widthForAreaShare(PIP_MAX_AREA_SHARE, area));
}

/** The smallest usable width; never above the maximum (degenerate areas collapse to the max). */
export function minPipWidth(area: PipArea): number {
  return Math.min(Math.max(1, widthForAreaShare(PIP_MIN_AREA_SHARE, area)), maxPipWidth(area));
}

/** Clamps a raw width into [min, max] for the area. */
function clampPipWidth(width: number, area: PipArea): number {
  return Math.min(Math.max(width, minPipWidth(area)), maxPipWidth(area));
}

/**
 * Clamps a box into the area: the width is pulled back into the size bounds, the height follows
 * the 16:9 aspect, and the top-left corner is pushed back so the whole tile stays inside the
 * USABLE region (the area minus the excluded bottom band). A box bigger than the area
 * (impossible by construction, but defensive) collapses to the max tile at the top-left
 * corner. All values round to whole CSS pixels.
 */
export function clampPipBox(box: PipBox, area: PipArea): PipBox {
  if (area.width <= 0 || area.height <= 0) {
    return box;
  }
  const width = Math.round(clampPipWidth(box.width, area));
  const height = Math.round(width / TILE_ASPECT);
  const x = Math.round(Math.min(Math.max(box.x, 0), Math.max(0, area.width - width)));
  const y = Math.round(Math.min(Math.max(box.y, 0), Math.max(0, usableHeight(area) - height)));
  return { x, y, width, height };
}

/** The opening layout: ~20% of the area, anchored to the bottom-right inside margin+inset. */
export function defaultPipBox(area: PipArea): PipBox {
  if (area.width <= 0 || area.height <= 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  const width = Math.round(clampPipWidth(widthForAreaShare(PIP_DEFAULT_AREA_SHARE, area), area));
  const height = Math.round(width / TILE_ASPECT);
  return clampPipBox(
    {
      x: area.width - width - PIP_MARGIN_PX,
      y: usableHeight(area) - height - PIP_MARGIN_PX,
      width,
      height,
    },
    area,
  );
}

/** Moves a box by a pointer delta and clamps the result fully inside the area. */
export function movePipBox(box: PipBox, dx: number, dy: number, area: PipArea): PipBox {
  return clampPipBox({ ...box, x: box.x + dx, y: box.y + dy }, area);
}

/**
 * Resizes a box to a target width (anchored at the top-left corner; the caller re-clamps the
 * position through {@link clampPipBox}, so a tile grown against the right/bottom edge pushes
 * back inside instead of overflowing). The 30% area cap clamps the width live while dragging.
 */
export function resizePipBox(box: PipBox, targetWidth: number, area: PipArea): PipBox {
  return clampPipBox({ ...box, width: targetWidth }, area);
}

/**
 * The persisted PiP layout: position and width as FRACTIONS of the main area, so a restore
 * scales with the viewport before the clamp fixes any overflow.
 */
interface StoredPipLayout {
  readonly x: number;
  readonly y: number;
  readonly w: number;
}

function isFraction(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

/** Persists the box as area fractions. Storage failures (quota, blocked access) are swallowed. */
export function savePipLayout(
  storage: KeyValueStorage,
  box: PipBox,
  area: PipArea,
  key: string = PIP_STORAGE_KEY,
): void {
  if (area.width <= 0 || area.height <= 0 || box.width <= 0) {
    return;
  }
  const stored: StoredPipLayout = {
    x: box.x / area.width,
    y: box.y / area.height,
    w: box.width / area.width,
  };
  try {
    storage.setItem(key, JSON.stringify(stored));
  } catch {
    // Best-effort: losing the remembered PiP corner is not worth failing a gesture over.
  }
}

/**
 * Restores the remembered layout for the given area: fractions scale back to pixels, then the
 * clamp pulls the box inside the current area (a smaller viewport, the 30% cap, the minimum).
 * Returns null when nothing usable is stored (missing, invalid JSON, out-of-range fractions).
 */
export function loadPipLayout(
  storage: KeyValueStorage,
  area: PipArea,
  key: string = PIP_STORAGE_KEY,
): PipBox | null {
  if (area.width <= 0 || area.height <= 0) {
    return null;
  }
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const { x, y, w } = record;
  if (!isFraction(x) || !isFraction(y) || !isFraction(w) || w === 0) {
    return null;
  }
  return clampPipBox(
    {
      x: x * area.width,
      y: y * area.height,
      width: w * area.width,
      height: (w * area.width) / TILE_ASPECT,
    },
    area,
  );
}
